import { useEffect, useState, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";
import { AppUpdate, AppUpdateAvailability } from "@capawesome/capacitor-app-update";

import api, { clearAuth, restoreAuthFromStorage } from "./api/Client";
import { getMinVersions } from "./api/version";
import Header from "./components/Header";
import Footer from "./components/Footer";
import BackHandler from "./components/BackHandler";
import ScrollToTop from "./components/ScrollToTop";
import Home from "./pages/Home";
import Login from "./pages/Login";
import ProductPage from "./pages/ProductPage";
import ServiceProductsPage from "./pages/ServiceProductsPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import CalendarPage from "./pages/CalendarPage";
import StatisticsPage from "./pages/StatisticsPage";
import UserPage from "./pages/UserPage";
import AddCouponsPage from "./pages/User/AddCouponsPage";
import FavoritesPage from "./pages/User/FavoritesPage";
import ViewPaymentMethodsPage from "./pages/User/ViewPaymentMethodsPage";
import DocumentPage from "./pages/User/DocumentPage";
import ElectronicWalletPage from "./pages/User/EWalletPage";
import RecurrentBookings from "./pages/RecurrentBookings";
import ConfigurationPage from "./pages/User/ConfigurationPage";
import ChangePasswordPage from "./pages/User/ChangePasswordPage";
import ProfilePage from "./pages/User/ProfilePage";
import EditProfilePage from "./pages/User/EditProfile";
import RecoverPasswordPage from "./pages/RecoverPasswordPage";
import PublicServicePage from "./pages/PublicServicePage";
import PublicServiceProductPage from "./pages/PublicServiceProductPage";
import NotificationsPage from "./pages/NotificationsPage";
import "./App.css";

const LAYOUT_HIDDEN_PATHS = new Set([
  "/",
  "/home",
  "/login",
  "/register",
  "/recover-password"
]);

const FOOTER_HIDDEN_PATHS = new Set([
  "/",
  "/home",
  "/login",
  "/register",
  "/change-password",
  "/settings",
  "/ewallet",
  "/payment-methods",
  "/profile",
  "/documents",
  "/profile/edit",
  "/recover-password",
  "/public-services"
]);

function isLowerVersion(current: string, required: string): boolean {
  const currentParts = (current || "").split(".").map((value) => parseInt(value, 10) || 0);
  const requiredParts = (required || "").split(".").map((value) => parseInt(value, 10) || 0);
  const length = Math.max(currentParts.length, requiredParts.length);

  for (let index = 0; index < length; index += 1) {
    const currentValue = currentParts[index] || 0;
    const requiredValue = requiredParts[index] || 0;

    if (currentValue < requiredValue) return true;
    if (currentValue > requiredValue) return false;
  }

  return false;
}

function FullScreenMessage({ children }: { children: ReactNode }) {
  return <div className="app-state-screen">{children}</div>;
}

export default function App() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [booting, setBooting] = useState(true);
  const [initialPath, setInitialPath] = useState<string | null>(null);
  const [forceUpdate, setForceUpdate] = useState(false);

  const hideLayout = LAYOUT_HIDDEN_PATHS.has(pathname);
  const hideFooter = FOOTER_HIDDEN_PATHS.has(pathname) || pathname.startsWith("/public-service/");

  useEffect(() => {
    const footerBase = 94;

    const setViewportVars = () => {
      const viewport = window.visualViewport;
      const height = (viewport?.height ?? window.innerHeight) * 0.01;
      const top = viewport?.offsetTop ?? 0;
      const left = viewport?.offsetLeft ?? 0;
      const bottom = Math.max(
        window.innerHeight - ((viewport?.height ?? window.innerHeight) + top),
        0
      );
      const right = Math.max(
        window.innerWidth - ((viewport?.width ?? window.innerWidth) + left),
        0
      );

      document.documentElement.style.setProperty("--vh", `${height}px`);
      document.documentElement.style.setProperty("--safe-top", `${top}px`);
      document.documentElement.style.setProperty("--safe-right", `${right}px`);
      document.documentElement.style.setProperty("--safe-bottom", `${bottom}px`);
      document.documentElement.style.setProperty("--safe-left", `${left}px`);
      document.documentElement.style.setProperty("--footer-height", `${footerBase + bottom}px`);
      document.documentElement.style.setProperty("--app-footer-height", `${footerBase + bottom}px`);
    };

    setViewportVars();

    window.visualViewport?.addEventListener("resize", setViewportVars);
    window.visualViewport?.addEventListener("scroll", setViewportVars);
    window.addEventListener("resize", setViewportVars);

    return () => {
      window.visualViewport?.removeEventListener("resize", setViewportVars);
      window.visualViewport?.removeEventListener("scroll", setViewportVars);
      window.removeEventListener("resize", setViewportVars);
    };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        if (!Capacitor.isNativePlatform()) {
          return;
        }

        const platform = Capacitor.getPlatform();
        const versionInfo = await getMinVersions();
        const appInfo = await CapacitorApp.getInfo();

        if (platform === "android") {
          const current = parseInt(String(appInfo.build ?? appInfo.version ?? 0), 10);
          const required =
            typeof versionInfo.minAndroid === "number"
              ? versionInfo.minAndroid
              : parseInt(String(versionInfo.minAndroid ?? 0), 10);

          if (current < required) {
            setForceUpdate(true);

            try {
              const updateInfo = await AppUpdate.getAppUpdateInfo();
              if (updateInfo.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE) {
                await AppUpdate.performImmediateUpdate();
              }
            } catch (error) {
              console.warn("No se pudo iniciar la actualizacion inmediata:", error);
            }
          }
        }

        if (platform === "ios") {
          const currentName = appInfo.version ?? "";
          const requiredName = String(versionInfo.minIos ?? "");

          if (isLowerVersion(currentName, requiredName)) {
            setForceUpdate(true);

            try {
              if ((versionInfo as any).storeUrlIos) {
                window.open((versionInfo as any).storeUrlIos, "_system");
              } else {
                await AppUpdate.openAppStore();
              }
            } catch (error) {
              console.warn("No se pudo abrir la App Store:", error);
            }
          }
        }
      } catch (error) {
        console.warn("Error comprobando versiones minimas de la app:", error);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { access, user: restoredUser } = await restoreAuthFromStorage({ refreshSession: true });

        if (access && restoredUser) {
          setUser(restoredUser);
          setInitialPath("/product");
          return;
        }

        setUser(null);
        setInitialPath("/home");
      } catch {
        setUser(null);
        setInitialPath("/home");
      } finally {
        setBooting(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    void StatusBar.setOverlaysWebView({ overlay: true });
    void StatusBar.setStyle({ style: Style.Dark });
  }, []);

  if (booting) {
    return <FullScreenMessage>Cargando sesion...</FullScreenMessage>;
  }

  if (forceUpdate) {
    return (
      <FullScreenMessage>
        Hay una nueva version de la aplicacion disponible. Debes actualizarla para continuar.
      </FullScreenMessage>
    );
  }

  return (
    <>
      <BackHandler />
      <ScrollToTop />

      <div className={hideLayout ? undefined : "shell-app"}>
        {!hideLayout ? <Header /> : null}

        <div
          className={
            hideLayout ? undefined : hideFooter ? "shell-main shell-main-no-footer" : "shell-main"
          }
        >
          <Routes>
            <Route path="/" element={<Navigate to={initialPath || "/home"} replace />} />
            <Route path="/home" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/recover-password" element={<RecoverPasswordPage />} />
            <Route path="/register" element={<Navigate to="/login" replace />} />
            <Route path="/product" element={<ProductPage />} />
            <Route path="/product/:productId" element={<ProductDetailPage />} />
            <Route path="/service-products/:serviceId" element={<ServiceProductsPage />} />
            <Route path="/public-services" element={<PublicServicePage />} />
            <Route path="/public-service/:serviceId" element={<PublicServiceProductPage />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/statistics" element={<StatisticsPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/profile/edit" element={<EditProfilePage />} />
            <Route path="/favorites" element={<FavoritesPage />} />
            <Route path="/recurrent-bookings" element={<RecurrentBookings />} />
            <Route path="/payment-methods" element={<ViewPaymentMethodsPage />} />
            <Route path="/documents" element={<DocumentPage />} />
            <Route path="/add-coupons" element={<AddCouponsPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route
              path="/settings"
              element={
                <ConfigurationPage
                  onLogout={async () => {
                    try {
                      await clearAuth();
                      navigate("/home", { replace: true });
                    } catch (error) {
                      console.error("Error en logout:", error);
                    }
                  }}
                  onDeleteAccount={async (password: string) => {
                    try {
                      if (!user?.email) {
                        alert("No se encontro el email del usuario");
                        return;
                      }

                      await api.post("/api/mobile/user", {
                        email: user.email,
                        password
                      });

                      alert("Cuenta eliminada correctamente");
                      await clearAuth();
                      navigate("/home", { replace: true });
                    } catch (error: any) {
                      console.error("Error borrando cuenta:", error);
                      alert(error?.response?.data?.error || "Error al eliminar la cuenta");
                    }
                  }}
                  onChangePasswordRequested={() => {
                    navigate("/change-password");
                  }}
                />
              }
            />
            <Route path="/change-password" element={<ChangePasswordPage />} />
            <Route path="/user" element={<UserPage />} />
            <Route path="/ewallet" element={<ElectronicWalletPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>

        {!hideLayout && !hideFooter ? <Footer /> : null}
      </div>
    </>
  );
}
